<template>
  <VeeForm
    ref="customerFormRef"
    v-slot="{ handleSubmit }"
    :validation-schema="validationSchema"
    :initial-values="initialFormValues"
    as="div"
  >
    <form @submit="handleSubmit($event, onContinue)">
      <div class="mt-2 bg-white p-4 rounded-md">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
          {{ $t("Client informations") }}
        </p>
        <div class="grid grid-cols-2 gap-x-4 gap-y-3">
          <div class="flex-1">
            <label for="firstName" class="block text-sm text-gray-700">
              {{ $t("First Name") }}
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.firstName"
              id="firstName"
              autocomplete="given-name"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="John"
              v-model="localCustomer.firstName"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.firstName" class="mt-2 text-sm text-red-600" />
          </div>

          <div class="flex-1">
            <label for="lastName" class="block text-sm text-gray-700">
              {{ $t("Last Name") }}
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.lastName"
              id="lastName"
              autocomplete="family-name"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Doe"
              v-model="localCustomer.lastName"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.lastName" class="mt-2 text-sm text-red-600" />
          </div>

          <div class="flex-1">
            <label for="email" class="block text-sm text-gray-700">
              {{ $t("Customer email") }}
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              id="email"
              name="customer.email"
              type="email"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="customer@gmail.com"
              v-model="localCustomer.email"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.email" class="mt-2 text-sm text-red-600" />
          </div>

          <PhoneNumberFormField v-model="localCustomer.phone" :shipping-address="selectedStore?.shippingAddress" />

          <!-- Birth Information Fields -->
          <template v-if="showBirthInfo">
            <div class="flex-1">
              <label for="birthDate" class="block text-sm text-gray-700">
                {{ $t("Birth date") }}
                <span class="ml-1 text-red-600">*</span>
              </label>
              <Field
                type="date"
                name="customer.birthDate"
                id="birthDate"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="01/01/1970"
                v-model="localCustomer.birthDate"
                @keydown="blockEnterBehavior"
              />
              <ErrorMessage name="customer.birthDate" class="mt-2 text-sm text-red-600" />
            </div>

            <div class="flex-1">
              <label for="birthCity" class="block text-sm text-gray-700">
                {{ $t("Birth city") }}
                <span class="ml-1 text-red-600">*</span>
              </label>
              <Field
                type="text"
                name="customer.birthCity"
                id="birthCity"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Paris"
                v-model="localCustomer.birthCity"
                @keydown="blockEnterBehavior"
              />
              <ErrorMessage name="customer.birthCity" class="mt-2 text-sm text-red-600" />
            </div>

            <CountryListboxField
              :model-value="localCustomer.birthCountry"
              @update:model-value="updateCustomerField('birthCountry', $event)"
              name="customer.birthCountry"
              :label="$t('Birth country')"
              :placeholder="$t('Select a country')"
              :required="true"
            />

            <CountryListboxField
              :model-value="localCustomer.nationality"
              @update:model-value="updateCustomerField('nationality', $event)"
              name="customer.nationality"
              :label="$t('Nationality')"
              :placeholder="$t('Select a country')"
              :required="true"
            />

            <div class="flex-1">
              <label for="buyerRole" class="block text-sm text-gray-700">
                {{ $t("Profession") }}
                <span class="ml-1 text-red-600">*</span>
              </label>
              <Field
                type="text"
                name="customer.buyerRole"
                id="buyerRole"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="localCustomer.buyerRole"
                @keydown="blockEnterBehavior"
              />
              <ErrorMessage name="customer.buyerRole" class="mt-2 text-sm text-red-600" />
            </div>
          </template>

          <!-- Address Fields -->
          <template v-if="showAddress">
            <div class="flex-1">
              <GoogleAddress
                @newAddress="updateAddress"
                :addressError="effectiveAddressError"
                :initialAddress="addressDisplayValue"
              />
            </div>

            <div class="flex-1">
              <label for="address2" class="block text-sm text-gray-700">{{ $t("Additional address") }}</label>
              <Field
                type="text"
                name="address.address2"
                id="address2"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Résidence, Etage..."
                v-model="localAddress2"
                @keydown="blockEnterBehavior"
              />
              <ErrorMessage name="address.address2" class="mt-2 text-sm text-red-600" />
            </div>
          </template>
        </div>

        <div class="mt-4 flex justify-end">
          <SecondaryButton type="submit">Continuer</SecondaryButton>
        </div>
      </div>
    </form>
  </VeeForm>
</template>

<script>
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import GoogleAddress from "@/components/address/GoogleAddress";
import PhoneNumberFormField from "../form/customer_form/PhoneNumberFormField.vue";
import CountryListboxField from "./customer_form/CountryListboxField.vue";
import { SecondaryButton } from "@estaly/ui";
import { mapGetters } from "vuex";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

export default {
  components: {
    VeeForm,
    Field,
    ErrorMessage,
    GoogleAddress,
    PhoneNumberFormField,
    CountryListboxField,
    SecondaryButton,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    validationSchema: {
      type: Object,
      required: true,
    },
    address: {
      type: Object,
      default: () => ({}),
    },
    address2: {
      type: String,
      default: "",
    },
    addressError: {
      type: Boolean,
      default: false,
    },
    showBirthInfo: {
      type: Boolean,
      default: false,
    },
    showAddress: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update:address2", "newAddress", "continue"],
  data() {
    return {
      localAddress2: this.address2 || "",
      addressValidationError: false,
    };
  },
  computed: {
    ...mapGetters("store", ["selectedStore"]),

    effectiveAddressError() {
      return this.addressError || this.addressValidationError;
    },

    addressDisplayValue() {
      const a = this.address;
      if (!a?.address1?.trim?.()) return "";
      const parts = [a.address1];
      if (a.zipCode || a.city) parts.push([a.zipCode, a.city].filter(Boolean).join(" "));
      if (a.country) parts.push(a.country);
      return parts.join(", ");
    },

    initialFormValues() {
      const values = { customer: this.modelValue || {} };
      if (this.showAddress) {
        values.address = {
          address1: this.address?.address1 || "",
          city: this.address?.city || "",
          country: this.address?.country || "",
          address2: this.address2 || "",
        };
      }
      return values;
    },

    localCustomer: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  methods: {
    onContinue() {
      if (this.showAddress) {
        const hasAddress = this.address?.address1?.trim?.();
        if (!hasAddress) {
          this.addressValidationError = true;
          return;
        }
      }
      this.addressValidationError = false;
      this.$emit("continue");
    },
    blockEnterBehavior,
    updateAddress(value) {
      this.$emit("newAddress", value);
    },
    updateCustomerField(field, value) {
      this.$emit("update:modelValue", { ...this.modelValue, [field]: value });
    },
  },
  watch: {
    address2: {
      immediate: true,
      handler(newVal) {
        this.localAddress2 = newVal;
      },
    },
    localAddress2(newVal) {
      this.$emit("update:address2", newVal);
    },
    address: {
      deep: true,
      handler(newAddress) {
        if (!this.showAddress || !newAddress) return;
        if (newAddress?.address1?.trim?.()) {
          this.addressValidationError = false;
        }
      },
    },
  },
};
</script>
